import { useEffect, useState } from 'react';

type TImageTone = 'light' | 'dark';

// Limiar de luminância (0-255) para classificar a região como escura.
// Usa coeficientes Rec. 709 para luminância perceptual.
const DARK_THRESHOLD = 140;

// Região amostrada no canto superior direito da imagem — bate com a
// posição do menu de opções (top-2 right-2, w-6 h-6 sobre aspect-3/2).
const SAMPLE_WIDTH_RATIO = 0.12;
const SAMPLE_HEIGHT_RATIO = 0.18;

const useImageBrightness = (src?: string): TImageTone => {
	const [tone, setTone] = useState<TImageTone>('light');

	useEffect(() => {
		if (!src) {
			setTone('light');
			return;
		}

		let cancelled = false;
		const img = new Image();
		img.crossOrigin = 'anonymous';

		img.onload = () => {
			if (cancelled) return;
			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d');
			if (!ctx) return;

			const sampleW = Math.max(1, Math.round(img.naturalWidth * SAMPLE_WIDTH_RATIO));
			const sampleH = Math.max(1, Math.round(img.naturalHeight * SAMPLE_HEIGHT_RATIO));

			canvas.width = sampleW;
			canvas.height = sampleH;
			ctx.drawImage(
				img,
				img.naturalWidth - sampleW,
				0,
				sampleW,
				sampleH,
				0,
				0,
				sampleW,
				sampleH
			);

			try {
				const { data } = ctx.getImageData(0, 0, sampleW, sampleH);
				let total = 0;
				const pixels = data.length / 4;
				for (let i = 0; i < data.length; i += 4) {
					const r = data[i] ?? 0;
					const g = data[i + 1] ?? 0;
					const b = data[i + 2] ?? 0;
					total += 0.299 * r + 0.587 * g + 0.114 * b;
				}
				const avg = total / pixels;
				setTone(avg < DARK_THRESHOLD ? 'dark' : 'light');
			} catch {
				setTone('light');
			}
		};

		img.onerror = () => {
			if (!cancelled) setTone('light');
		};

		img.src = src;

		return () => {
			cancelled = true;
			img.onload = null;
			img.onerror = null;
		};
	}, [src]);

	return tone;
};

export { useImageBrightness };
export type { TImageTone };
